import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight } from "lucide-react";

interface DeviceStatus {
  id: string;
  name: string | null;
  serial_number: string | null;
  status: 'online' | 'offline' | 'warning' | 'error';
  last_reading_at: string | null;
  uptime_seconds: number | null;
  last_24h_readings: number;
  health_score: number;
  location_id: string | null;
  location_name: string | null;
}

interface DeviceStatusTableProps {
  devices: DeviceStatus[];
}

export function DeviceStatusTable({ devices }: DeviceStatusTableProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500/20 text-green-700 dark:bg-green-500/20 dark:text-green-300';
      case 'warning': return 'bg-yellow-500/20 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300';
      case 'error': return 'bg-red-500/20 text-red-700 dark:bg-red-500/20 dark:text-red-300';
      default: return 'bg-gray-500/20 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300';
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatLastSeen = (lastReadingAt: string | null) => {
    if (!lastReadingAt) return 'Never';
    try {
      return formatDistanceToNow(new Date(lastReadingAt), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Device</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Last Reading</TableHead>
            <TableHead>24h Readings</TableHead>
            <TableHead>Health Score</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => (
            <TableRow key={device.id} className="group">
              <TableCell className="font-medium">
                <Link
                  to="/dashboard/devices/$deviceId"
                  params={{ deviceId: device.id }}
                  className="text-sm text-muted-foreground font-medium hover:text-primary mt-1 block"
                >
                  {device.name || device.serial_number || 'Unnamed Device'}
                </Link>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={getStatusColor(device.status)}>
                  {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>{device.location_name || 'Unassigned'}</TableCell>
              <TableCell>{formatLastSeen(device.last_reading_at)}</TableCell>
              <TableCell>{device.last_24h_readings.toLocaleString()}</TableCell>
              <TableCell>
                <span className={getHealthColor(device.health_score)}>
                  {device.health_score}%
                </span>
              </TableCell>
              <TableCell>
                <Link
                  to="/dashboard/devices/$deviceId"
                  params={{ deviceId: device.id }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
